import * as tslib_1 from "tslib";
import { OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TWO_MINUTES_IN_MILLIS } from '@core/consts';
import { SidenavService } from '@core/services/sidenav.service';
import { endOfDay, mapIds, startOfDay } from '@core/utils/filters-utils';
import { environment } from '@environment';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { DateIntervalValidation } from '@validators/date-interval-validation';
import { DatePeriodValidation } from '@validators/date-period-validation';
import { DateValidation } from '@validators/date.validation';
import { isEqual } from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { sentry } from 'sentry';
import { ReportsService } from '../reports.service';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
var DashboardMaintenancesComponent = /** @class */ (function () {
    function DashboardMaintenancesComponent(sidenavService, fuseTranslationLoaderService, router, fuseProgressBarService, reportsService) {
        this.sidenavService = sidenavService;
        this.fuseTranslationLoaderService = fuseTranslationLoaderService;
        this.router = router;
        this.fuseProgressBarService = fuseProgressBarService;
        this.reportsService = reportsService;
        this.isLoading = true;
        this.isFilterOn = false;
        this.totalMaintenances = 0;
        this.isFilterExpanded = false;
        this.accountMaintenancesStatusInterval = {
            responseAverage: '',
            solutionAverage: '',
            pending: '',
            scheduled: '',
            inProgress: '',
            reported: '',
            requested: '',
            onNegotiation: '',
        };
        this.status = {
            new: 0,
            pending: 0,
            requested: 0,
            onNegotiation: 0,
            scheduled: 0,
            inProgress: 0,
            done: 0,
            reported: 0,
            canceled: 0,
        };
        this.subscriptions = new Subject();
        this.defaultFilterValue = {
            openedAtGte: moment().subtract(1, 'month'),
            openedAtLte: moment(),
            locations: null,
            maintenanceTypes: null,
            segments: null,
            companies: null,
            locationGroups: null,
            scheduledAtGte: null,
            scheduledAtLte: null,
            closedAtGte: null,
            closedAtLte: null,
        };
        this.filterForm = new FormGroup({
            openedAtGte: new FormControl(this.defaultFilterValue.openedAtGte, [Validators.required, DateValidation.validate]),
            openedAtLte: new FormControl(this.defaultFilterValue.openedAtLte, [Validators.required, DateValidation.validate]),
            locations: new FormControl(this.defaultFilterValue.locations),
            maintenanceTypes: new FormControl(this.defaultFilterValue.maintenanceTypes),
            segments: new FormControl(this.defaultFilterValue.segments),
            companies: new FormControl(this.defaultFilterValue.companies),
            locationGroups: new FormControl(this.defaultFilterValue.locationGroups),
            scheduledAtGte: new FormControl(this.defaultFilterValue.scheduledAtGte, DateValidation.validate),
            scheduledAtLte: new FormControl(this.defaultFilterValue.scheduledAtLte, DateValidation.validate),
            closedAtGte: new FormControl(this.defaultFilterValue.closedAtGte, DateValidation.validate),
            closedAtLte: new FormControl(this.defaultFilterValue.closedAtLte, DateValidation.validate)
        }, {
            validators: [
                DateIntervalValidation.validate({
                    from: 'openedAtGte',
                    to: 'openedAtLte',
                    maxDiffLte: 1,
                    unit: 'year'
                }),
                DatePeriodValidation.validate(['openedAt', 'closedAt', 'scheduledAt'])
            ]
        });
        this.fuseTranslationLoaderService.loadJsonTranslations({ english: english, portuguese: portuguese, spanish: spanish });
    }
    DashboardMaintenancesComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.updateAutoRefreshState({ enabled: false });
        this.filterForm.valueChanges.pipe(takeUntil(this.subscriptions), filter(function () { return _this.filterForm.valid; }), debounceTime(200), tap(function () { return _this.showLoading(); }), tap(function () {
            var currentWhere = _this.toQuery(_this.filterForm.value);
            var defaultWhere = _this.toQuery(_this.defaultFilterValue);
            _this.isFilterOn = !isEqual(currentWhere, defaultWhere);
        }), switchMap(function () { return _this.updateData(); }), tap(function () { return _this.hideLoading(); })).subscribe();
        this.reload();
    };
    DashboardMaintenancesComponent.prototype.ngOnDestroy = function () {
        this.subscriptions.next();
        this.subscriptions.complete();
        clearInterval(this.intervalId);
    };
    DashboardMaintenancesComponent.prototype.toQuery = function (value) {
        return {
            locationGroupId_in: mapIds(value.locationGroups),
            locationId_in: mapIds(value.locations),
            typeId_in: mapIds(value.maintenanceTypes),
            segmentId_in: mapIds(value.segments),
            companyId_in: mapIds(value.companies),
            from: startOfDay(value.openedAtGte),
            to: endOfDay(value.openedAtLte),
            scheduledAt_gte: startOfDay(value.scheduledAtGte, 'YYYY-MM-DD'),
            scheduledAt_lte: endOfDay(value.scheduledAtLte, 'YYYY-MM-DD'),
            closedAt_gte: startOfDay(value.closedAtGte),
            closedAt_lte: endOfDay(value.closedAtLte),
        };
    };
    DashboardMaintenancesComponent.prototype.updateData = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var where, _a, accountMaintenancesStatus, accountMaintenancesStatusInterval, accountMaintenancesVolumeCount, err_1;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        where = this.toQuery(this.filterForm.value);
                        return [4 /*yield*/, Promise.all([
                                this.reportsService.accountMaintenancesStatus(where),
                                this.reportsService.accountMaintenancesStatusInterval(where),
                                this.reportsService.accountMaintenancesVolumeCount(where)
                            ])];
                    case 1:
                        _a = tslib_1.__read.apply(void 0, [_b.sent(), 3]), accountMaintenancesStatus = _a[0], accountMaintenancesStatusInterval = _a[1], accountMaintenancesVolumeCount = _a[2];
                        this.status = accountMaintenancesStatus.status;
                        this.totalMaintenances = accountMaintenancesStatus.totalMaintenances;
                        this.accountMaintenancesStatusInterval = accountMaintenancesStatusInterval;
                        this.accountMaintenancesVolumeCount = accountMaintenancesVolumeCount;
                        return [3 /*break*/, 3];
                    case 2:
                        err_1 = _b.sent();
                        sentry.captureException(err_1);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    DashboardMaintenancesComponent.prototype.buildQueryParams = function () {
        var query = this.toQuery(this.filterForm.value);
        return {
            locationGroupId_in: query.locationGroupId_in && query.locationGroupId_in.join(','),
            locationId_in: query.locationId_in && query.locationId_in.join(','),
            typeId_in: query.typeId_in && query.typeId_in.join(','),
            segmentId_in: query.segmentId_in && query.segmentId_in.join(','),
            companyId_in: query.companyId_in && query.companyId_in.join(','),
            openedAt_gte: startOfDay(query.from),
            openedAt_lte: endOfDay(query.to),
            scheduledAt_gte: startOfDay(query.scheduledAt_gte, 'YYYY-MM-DD'),
            scheduledAt_lte: endOfDay(query.scheduledAt_lte, 'YYYY-MM-DD'),
            closedAt_gte: startOfDay(query.closedAt_gte),
            closedAt_lte: endOfDay(query.closedAt_lte),
            archived: 'false'
        };
    };
    DashboardMaintenancesComponent.prototype.goToMaintenances = function (status, includeStatus) {
        if (includeStatus === void 0) { includeStatus = true; }
        var queryParams = this.buildQueryParams();
        if (status) {
            if (includeStatus) {
                queryParams.status_in = status.join(',');
            }
            else {
                queryParams.status_not_in = status.join(',');
            }
        }
        var url = this.router.serializeUrl(this.router.createUrlTree(['manutencoes'], { queryParams: tslib_1.__assign({}, queryParams) }));
        var absoluteUrl = environment.MOUNTDOOM_URL + "/#" + url;
        try {
            window.open(absoluteUrl);
        }
        catch (error) {
            console.log('erro: 657aeae2-bc2f-4a2b-8eef-1181405b8b7b');
            console.error(error);
            sentry.captureException(error);
        }
    };
    DashboardMaintenancesComponent.prototype.updateAutoRefreshState = function (_a) {
        var _this = this;
        var enabled = _a.enabled;
        if (enabled) {
            this.intervalId = setInterval(function () { return _this.reload(); }, TWO_MINUTES_IN_MILLIS);
        }
        else {
            clearInterval(this.intervalId);
        }
    };
    DashboardMaintenancesComponent.prototype.clearFilters = function () {
        this.filterForm.setValue(this.defaultFilterValue);
    };
    DashboardMaintenancesComponent.prototype.showLoading = function () {
        this.isLoading = true;
        this.fuseProgressBarService.show();
    };
    DashboardMaintenancesComponent.prototype.hideLoading = function () {
        this.isLoading = false;
        this.fuseProgressBarService.hide();
    };
    DashboardMaintenancesComponent.prototype.reload = function () {
        this.filterForm.updateValueAndValidity();
    };
    DashboardMaintenancesComponent.prototype.beforePrint = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                this.isFilterExpanded = true;
                return [2 /*return*/];
            });
        });
    };
    return DashboardMaintenancesComponent;
}());
export { DashboardMaintenancesComponent };
